import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileCheck, FileText } from "lucide-react";
import { toast } from "sonner";

const money = (v) => `${Number(v || 0).toLocaleString("fr-FR")} Dhs`;

export default function ReceiptsList() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const canView = hasRole("super_admin", "direction", "administration", "finance");
  const [rows, setRows] = useState([]);

  useEffect(() => { api.get("/finance/receipts").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail))); }, []);
  const openPdf = async (id) => { try { const r = await api.get(`/finance/receipts/${id}/pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  if (!canView) return <div className="p-8 text-center text-slate-400" data-testid="access-denied">{t("accessDenied")}</div>;

  return (
    <div data-testid="receipts-page">
      <div className="flex items-center gap-3 mb-4"><FileCheck className="h-6 w-6 text-primary" /><h1 className="text-2xl font-display font-bold text-slate-900">{t("receipts")}</h1></div>
      <Card className="card-float border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/70"><TableHead>{t("number")}</TableHead><TableHead>{t("family")}</TableHead><TableHead>{t("method")}</TableHead><TableHead className="text-right">{t("amount")}</TableHead><TableHead>{t("date")}</TableHead><TableHead>{t("status")}</TableHead><TableHead className="text-right">{t("actions")}</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">{t("noData")}</TableCell></TableRow> :
              rows.map((r) => (
                <TableRow key={r.id} data-testid="receipt-row">
                  <TableCell className="font-medium text-slate-800">{r.number}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{r.family_name}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{t(`method_${r.method}`)}</TableCell>
                  <TableCell className="text-right font-semibold">{money(r.amount)}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{(r.date || "").slice(0, 10)}</TableCell>
                  <TableCell><Badge className={`border-0 text-xs ${r.status === "void" ? "bg-slate-200 text-slate-400" : "bg-emerald-100 text-emerald-700"}`}>{r.status === "void" ? t("voidReceipt") : t("active")}</Badge></TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="ghost" className="h-8" data-testid={`receipt-pdf-${r.id}`} onClick={() => openPdf(r.id)}><FileText className="h-4 w-4" /></Button></TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
